import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Vacation } from "domain/entities/vacations";
import { VacationInputFilter } from "domain/models/vacation-input-filter";
import { Logger } from "infra/logger/logger";
import vacationsRepository from "infra/repositories/vacations-repository";

export const useVacationPlanByFilter = (filter?: VacationInputFilter, enabled: boolean = true) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["vacationPlans", filter?.employeeId, filter?.startRange, filter?.endRange],
    queryFn: async () => {
      try {
        const result = await vacationsRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
    enabled: enabled,
  });
};

export const useVacationPlanById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["vacationPlan", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("Id field invalid");
        const result = await vacationsRepository.getById(id);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreateVacationPlanMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Vacation) => {
      const result = await vacationsRepository.create(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacationPlans"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
      }
    },
  });
};

export const useUpdateVacationPlanMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Vacation) => {
      const result = await vacationsRepository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacationPlans"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
      }

    },
  });
};

export const useDeleteVacationPlanMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await vacationsRepository.delete(id);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacationPlans"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata
        );
      }
    },
  });
};
